import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { InboxBell } from './InboxBell';
import { CommandPalette } from './CommandPalette';
import { ConfirmProvider } from './ui';
import { useState } from 'react';
import { BRAND } from '../brand';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme/ThemeProvider';
import { CookieConsentBanner } from './CookieConsentBanner';
import { ToastViewport } from './ToastViewport';

function Wordmark() {
  const { theme } = useTheme();
  const src = theme === 'dark' ? BRAND.logoSrc : BRAND.logoOriginal;
  return (
    <Link to="/" className="flex shrink-0 items-center" aria-label={BRAND.name}>
      <img src={src} alt={BRAND.name} className="h-10 w-auto sm:h-11" />
    </Link>
  );
}

/** The palette is keyboard-first, but nobody discovers ⌘K without being told. */
function SearchButton() {
  const fire = () =>
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
  return (
    <button
      type="button"
      onClick={fire}
      aria-label="Search"
      className="hidden items-center gap-2 rounded-md border border-ink-800 px-2.5 py-1.5 text-xs text-cream-300/60 transition hover:border-gold-500/40 hover:text-cream-100 lg:flex"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
      </svg>
      Search
      <kbd className="rounded border border-ink-800 px-1 font-sans text-[10px]">⌘K</kbd>
    </button>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className="grid h-11 w-11 place-items-center rounded-full border border-gold-600/40 text-cream-200 transition hover:border-gold-400 hover:text-gold-200"
    >
      {isDark ? (
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      )}
    </button>
  );
}

const AUTH_LINKS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/club', label: 'Club' },
  { to: '/tastings', label: 'Tastings' },
  { to: '/cellar', label: 'Cellar' },
  { to: '/archive', label: 'Archive' },
  { to: '/account', label: 'Account' },
  { to: '/help', label: 'Help' },
] as const;

export function Layout() {
  const { user, logout, adminEligible, memberMode, toggleMemberMode } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `block py-2 text-sm md:inline md:py-0 ${isActive ? 'text-gold-200' : 'text-cream-200 hover:text-gold-200'}`;

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate('/');
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-ink-800/80 bg-ink-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Wordmark />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-4 md:flex xl:gap-5" aria-label="Main">
            <NavLink to="/" className={navClass} end>
              Home
            </NavLink>
            {user ? (
              <>
                {AUTH_LINKS.map(({ to, label }) => (
                  <NavLink key={to} to={to} className={navClass}>
                    {label}
                  </NavLink>
                ))}
                {adminEligible && !memberMode && (
                  <button
                    type="button"
                    className="btn-ghost whitespace-nowrap px-3 py-1 text-xs"
                    title="See the site exactly as a member does"
                    onClick={() => void toggleMemberMode()}
                  >
                    View as member
                  </button>
                )}
                <SearchButton />
                <InboxBell />
                <ThemeToggle />
                <button
                  type="button"
                  onClick={handleLogout}
                  className="btn-ghost whitespace-nowrap px-3 py-1 text-xs"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/help" className={navClass}>
                  Help
                </NavLink>
                <ThemeToggle />
                <Link to="/login" className="btn-primary px-3 py-1 text-xs">
                  Member Log in
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu toggle */}
          <div className="flex items-center gap-2 md:hidden">
            {user ? <InboxBell /> : null}
            <ThemeToggle />
            <button
              type="button"
              className="grid h-11 w-11 place-items-center rounded-md border border-ink-800 text-cream-100"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-5 w-5" stroke="currentColor" strokeWidth="2">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav
            id="mobile-nav"
            className="border-t border-ink-800/80 px-4 py-3 md:hidden"
            aria-label="Mobile"
          >
            <NavLink to="/" className={navClass} end onClick={() => setMenuOpen(false)}>
              Home
            </NavLink>
            {user ? (
              <>
                {AUTH_LINKS.map(({ to, label }) => (
                  <NavLink key={to} to={to} className={navClass} onClick={() => setMenuOpen(false)}>
                    {label}
                  </NavLink>
                ))}
                {adminEligible && !memberMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      void toggleMemberMode();
                    }}
                    className="btn-ghost mt-2 w-full py-2 text-xs"
                  >
                    View as member
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="btn-ghost mt-2 w-full py-2 text-xs"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/help" className={navClass} onClick={() => setMenuOpen(false)}>
                  Help
                </NavLink>
                <Link
                  to="/login"
                  className="btn-primary mt-2 block py-2 text-center text-xs"
                  onClick={() => setMenuOpen(false)}
                >
                  Member Log in
                </Link>
              </>
            )}
          </nav>
        )}

        {/* Member view has to be unmistakable: an admin who forgets they're in
            it will wonder where their tools went, and it survives a reload. */}
        {adminEligible && memberMode && (
          <div role="status" className="border-t border-gold-600/40 bg-gold-600/15">
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2">
              <p className="text-sm text-cream-100">
                You're viewing as a member.{' '}
                <span className="text-cream-300/70">Admin tools are hidden.</span>
              </p>
              <button
                type="button"
                className="btn-primary px-3 py-1 text-xs"
                onClick={() => void toggleMemberMode()}
              >
                Switch back to admin
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <Outlet />
      </main>

      <footer className="border-t border-ink-800/80 px-4 py-8 text-center">
        <p className="mx-auto max-w-xl font-display text-sm italic text-cream-300/70">
          “{BRAND.tagline}”
        </p>
        <p className="mt-3 text-xs text-cream-300/40">
          {BRAND.name} · {BRAND.location} · Est. {BRAND.established}
        </p>
      </footer>

      {user && <CookieConsentBanner />}
      {/* Search requires auth, so only mount the ⌘K handler for signed-in members. */}
      {user && <CommandPalette />}
      <ToastViewport />
    </div>
  );
}

/** Confirm dialogs need a host above every page that might raise one. */
export function LayoutWithProviders() {
  return (
    <ConfirmProvider>
      <Layout />
    </ConfirmProvider>
  );
}
